import * as moment from 'moment';

import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../../../../../../../../../services/customer/customer.service';
import { dateStringFromISO8601, propExists } from '../../../../../../../../../../utils';
import { CustomerMembership, CustomerMembershipCustomer } from '../../../../../../../../../../models/membership';
import { OrganizationConfigService } from '../../../../../../../../../../services/organization-config/organization-config.service';

@Component({
  selector: 'app-edit-recurring-customer-membership-freeze-individual',
  templateUrl: './edit-recurring-customer-membership-freeze-individual.component.html',
  styleUrls: ['./edit-recurring-customer-membership-freeze-individual.component.css']
})
export class EditRecurringCustomerMembershipFreezeIndividualComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  public member: CustomerMembershipCustomer;
  public effectiveDate: string = null;
  public unfreezeDate: string = null;
  public reason: string = null;

  // The number of days from now (positive = future, negative = past) of the
  // selected effective date.
  public effectiveDaysFromNow: number = null;

  public errorMessage = '';

  public dateStringFromISO8601 = dateStringFromISO8601;

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfigService: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    // Reload the org config, in case the membership freeze reason policy has changed.
    this.orgConfigService.loadOrganizationConfig();

    for (const member of this.getActionableMembers()) {
      this.member = member
    }
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  public updateEffectiveDaysFromNow() {
    if (!propExists(this.effectiveDate)) {
      this.effectiveDaysFromNow = null;
    } else {
      this.effectiveDaysFromNow = moment(this.effectiveDate).diff(moment(), 'days');
    }
    this.errorMessage = '';
  }

  public getActionableMembers(): CustomerMembershipCustomer[] {
    return this.membership.all_customers.filter(cmc => ((!cmc.is_individually_frozen) && (cmc.member_freeze_date === null)));
  }

  public getPossibleUnfreezeDates(): string[] {
    if (!this.effectiveDate) { return []; }

    return this.membership.upcoming_bill_dates.filter(ubd => ubd > this.effectiveDate);
  }

  private handleSave() {
    const orgConfig = this.orgConfigService.getOrganizationConfig();
    if (this.effectiveDate === null) {
      this.errorMessage = 'Please select a date for the freeze to take effect.';
      return;
    }
    if (orgConfig.require_membership_freeze_reason && (this.reason === null)) {
      this.errorMessage = 'Please select a reason for the freeze.';
      return;
    }

    this.loadingEvent.emit(true);

    this.errorMessage = '';

    this.customerService
      .freezeMemberOnDate(
        this.member,
        this.membership,
        this.effectiveDate,
        this.unfreezeDate,
        this.reason
      )
      .then(() => {
        this.savedCallback(this.member.first_name + ' ' + this.member.last_name + ' successfully scheduled to be frozen.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to freeze this member.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => { this.loadingEvent.emit(false); });
  }

}
